import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment-timezone";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import Container from "../../../componentLibrary/Container";
import Button from "../../../componentLibrary/Button";
import Input from "../../../componentLibrary/Input";
import ExternalTextEditor from "../../../componentLibrary/ExternalTextEditor";
import Combobox, { ComboboxOptionI } from "../../../componentLibrary/Combobox";
import ConfirmationBox from "../../../componentLibrary/ConfirmationBox";
import { useNotification } from "../../../hooks/useNotification";
import { useCreateAnnouncement } from "../../../api/mutations";
import { AnnouncementTypeEnum, AnnouncementPriorityEnum } from "../../../types";
import { CreateAnnouncementRequest } from "../../../api/types";
import { isQuillContentEmpty } from "../../../utils/quillUtils";

const blankAnnouncement = {
  title: "",
  message: "",
  type: AnnouncementTypeEnum.INFO,
  priority: AnnouncementPriorityEnum.MEDIUM,
  actions: [] as Array<{
    label: string;
    url?: string;
    variant?: "primary" | "secondary";
  }>,
  expiresAt: "",
  startsAt: moment().startOf("hour").format("YYYY-MM-DDTHH:mm"),
};

const AddAnnouncementComponent = () => {
  const [newAnnouncement, setNewAnnouncement] = useState(blankAnnouncement);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [editorKey, setEditorKey] = useState(0);

  const queryClient = useQueryClient();
  const { notify } = useNotification();

  const mutation = useCreateAnnouncement({
    onSuccess: async () => {
      notify("success", "Announcement created successfully");
      setNewAnnouncement(blankAnnouncement);
      setEditorKey((k) => k + 1);
      await queryClient.invalidateQueries({ queryKey: ["announcements"] });
    },
    onError: () => {
      notify("error", "Failed to create announcement");
    },
  });

  const handleSubmitAnnouncement = () => {
    if (!newAnnouncement.title.trim()) {
      notify("error", "Title is required");
      return;
    }
    if (isQuillContentEmpty(newAnnouncement.message)) {
      notify("error", "Message is required");
      return;
    }

    // ✅ UPDATE: Use proper type and ISO strings
    const payload: CreateAnnouncementRequest = {
      title: newAnnouncement.title,
      message: newAnnouncement.message,
      type: newAnnouncement.type,
      priority: newAnnouncement.priority,
      actions: newAnnouncement.actions.filter((action) => action.label.trim()),
      expiresAt: newAnnouncement.expiresAt
        ? new Date(newAnnouncement.expiresAt).toISOString()
        : null,
      startsAt: new Date(newAnnouncement.startsAt).toISOString(),
    };

    mutation.mutate(payload);
  };

  const handleCancel = () => {
    setNewAnnouncement(blankAnnouncement);
    setEditorKey((k) => k + 1);
    setShowCancelDialog(false);
  };

  const handleAddAction = () => {
    setNewAnnouncement((prev) => ({
      ...prev,
      actions: [...prev.actions, { label: "", url: "", variant: "primary" }],
    }));
  };

  const handleRemoveAction = (index: number) => {
    setNewAnnouncement((prev) => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index),
    }));
  };

  const handleActionChange = (
    index: number,
    field: "label" | "url" | "variant",
    value: string,
  ) => {
    setNewAnnouncement((prev) => {
      const newActions = [...prev.actions];
      newActions[index] = { ...newActions[index], [field]: value };
      return { ...prev, actions: newActions };
    });
  };

  return (
    <>
      <Container className="flex flex-col h-[83vh] relative overflow-hidden">
        {/* Sticky Header - always visible at top */}
        <header
          className="sticky top-0 z-10 bg-white flex flex-row justify-between items-center pb-3 border-b border-gray-200"
          aria-label="New announcement toolbar"
        >
          <h3 className="mb-0">Announcement</h3>
          <div className="flex flex-row space-x-2 items-center">
            <Button
              variant="alternative"
              onClick={() => setShowCancelDialog(true)}
            >
              <span className="text-red-400">Cancel</span>
            </Button>
            <Button
              onClick={handleSubmitAnnouncement}
              disabled={mutation.isPending}
            >
              Submit
            </Button>
          </div>
        </header>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title
              </label>
              <Input
                value={newAnnouncement.title}
                onChange={(e) =>
                  setNewAnnouncement((prev) => ({
                    ...prev,
                    title: e.target.value,
                  }))
                }
                placeholder="Enter announcement title"
              />
            </div>

            <div>
              <ExternalTextEditor
                key={editorKey}
                value={newAnnouncement.message}
                handleChange={(value) =>
                  setNewAnnouncement((prev) => ({
                    ...prev,
                    message: value,
                  }))
                }
                placeholder="Enter announcement message"
                label="Message"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type
                </label>
                <Combobox
                  options={[
                    { value: AnnouncementTypeEnum.INFO, label: "Info" },
                    { value: AnnouncementTypeEnum.UPDATE, label: "Update" },
                    { value: AnnouncementTypeEnum.FEATURE, label: "Feature" },
                  ]}
                  value={{
                    value: newAnnouncement.type,
                    label:
                      newAnnouncement.type.charAt(0).toUpperCase() +
                      newAnnouncement.type.slice(1),
                  }}
                  onChange={(option: ComboboxOptionI) =>
                    setNewAnnouncement((prev) => ({
                      ...prev,
                      type: option.value as AnnouncementTypeEnum,
                    }))
                  }
                  placeholder="Select type..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority
                </label>
                <Combobox
                  options={[
                    { value: AnnouncementPriorityEnum.LOW, label: "Low" },
                    { value: AnnouncementPriorityEnum.MEDIUM, label: "Medium" },
                    { value: AnnouncementPriorityEnum.HIGH, label: "High" },
                    {
                      value: AnnouncementPriorityEnum.CRITICAL,
                      label: "Critical",
                    },
                  ]}
                  value={{
                    value: newAnnouncement.priority,
                    label:
                      newAnnouncement.priority.charAt(0).toUpperCase() +
                      newAnnouncement.priority.slice(1),
                  }}
                  onChange={(option: ComboboxOptionI) =>
                    setNewAnnouncement((prev) => ({
                      ...prev,
                      priority: option.value as AnnouncementPriorityEnum,
                    }))
                  }
                  placeholder="Select priority..."
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Starts At
                </label>
                <Input
                  type="datetime-local"
                  value={newAnnouncement.startsAt}
                  onChange={(e) =>
                    setNewAnnouncement((prev) => ({
                      ...prev,
                      startsAt: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expires At (Optional)
                </label>
                <Input
                  type="datetime-local"
                  value={newAnnouncement.expiresAt}
                  onChange={(e) =>
                    setNewAnnouncement((prev) => ({
                      ...prev,
                      expiresAt: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="flex flex-col bg-[#F5F5F5] rounded-lg p-4 space-y-2 shadow-md border border-[#E0E0E0]">
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-sm font-semibold">Action Buttons</h4>
                <Button
                  variant="secondary"
                  onClick={handleAddAction}
                  className="flex items-center gap-2"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add Action
                </Button>
              </div>

              {newAnnouncement.actions.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  No actions added. Click &quot;Add Action&quot; to create a
                  button.
                </p>
              ) : (
                <div className="space-y-2">
                  {newAnnouncement.actions.map((action, index) => (
                    <div
                      key={index}
                      className="flex gap-2 items-start p-3 bg-white rounded-lg"
                    >
                      <div className="flex-1 space-y-2">
                        <Input
                          value={action.label}
                          onChange={(e) =>
                            handleActionChange(index, "label", e.target.value)
                          }
                          placeholder="Button label"
                        />
                        <Input
                          value={action.url || ""}
                          onChange={(e) =>
                            handleActionChange(index, "url", e.target.value)
                          }
                          placeholder="URL (e.g., /explore or https://example.com)"
                        />
                        <Combobox
                          options={[
                            { value: "primary", label: "Primary" },
                            { value: "secondary", label: "Secondary" },
                          ]}
                          value={{
                            value: action.variant || "primary",
                            label:
                              (action.variant || "primary")
                                .charAt(0)
                                .toUpperCase() +
                              (action.variant || "primary").slice(1),
                          }}
                          onChange={(option: ComboboxOptionI) =>
                            handleActionChange(index, "variant", option.value)
                          }
                          placeholder="Select variant..."
                        />
                      </div>
                      <Button.Circle
                        variant="cautionary"
                        onClick={() => handleRemoveAction(index)}
                        className="p-2"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </Button.Circle>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Container>

      <ConfirmationBox
        isOpen={showCancelDialog}
        onConfirm={handleCancel}
        onCancel={() => setShowCancelDialog(false)}
        onCloseBox={() => setShowCancelDialog(false)}
        title="Cancel Announcement"
        message="Are you sure you want to cancel? All unsaved changes will be lost."
        confirmText="Yes, Cancel"
        cancelText="No, Keep Editing"
      />
    </>
  );
};

export default AddAnnouncementComponent;
